/* eslint react-hooks/exhaustive-deps: 0 */ // --> OFF

import React, { useCallback, useEffect, useRef } from 'react'
import { useApp } from '../app-context'
import { formatDate } from '../../utils'
import future from '../../assets/future.svg'
import past from '../../assets/past.svg'
import { useIsInView, useViewport } from '@ddg-frontend/fe-essentials'

interface ViewControlsProps {
  children: React.ReactNode
  dayWidth?: number
  totalDays?: number
}

const DAY_WIDTH_PX = 9600

export const ViewControls = ({
  children,
  dayWidth = DAY_WIDTH_PX,
  totalDays,
}: ViewControlsProps) => {
  const viewRef = useRef<HTMLDivElement | null>(null)
  const {
    days,
    timeline,
    initTimeline,
    setOffsetFromUser,
    setOffsetProgrammatic,
    setTouched,
    setOriginalOffset,
    setSource,
  } = useApp()
  const { offsetX, originalOffset, touched, dateDisplayed, source } = timeline
  const computedTotalDays = totalDays ?? (Array.isArray(days) ? days.length : 0)
  const contentWidth = Math.max(0, computedTotalDays * dayWidth)

  const pixelPerHour = dayWidth / 24
  const pixelPerMinute = pixelPerHour / 60

  const viewport = useViewport(['s', 'xs'])
  const step = viewport === 'xs' ? pixelPerHour * 0.5 : pixelPerHour * 4

  const [inViewRef, isInView] = useIsInView<HTMLDivElement>({
    rootMargin: '0px',
    threshold: 0.000001,
  })

  const getViewportW = () =>
    viewRef.current?.clientWidth ??
    (typeof window !== 'undefined' ? window.innerWidth : 0) ??
    0

  const getMaxScroll = useCallback(
    () => Math.max(0, contentWidth - getViewportW()),
    [contentWidth]
  )

  const clampOffset = useCallback(
    (x: number) => Math.min(Math.max(x, 0), getMaxScroll()),
    [getMaxScroll]
  )

  const now = new Date()
  const currentMinutes = now.getHours() * 60 + now.getMinutes()

  const getTargetForDate = useCallback(
    (date: Date) => {
      const dayIndex =
        days?.findIndex((d) => d === date.toISOString().slice(0, 10)) ?? -1
      const halfViewport = getViewportW() / 2
      const pixelInDay = currentMinutes * pixelPerMinute
      const raw = Math.max(0, dayIndex) * dayWidth + pixelInDay - halfViewport
      return clampOffset(raw)
    },
    [days, currentMinutes, pixelPerMinute, dayWidth, clampOffset]
  )

  // ---- EFFECT 1: INIT once (compute initial offsets and sync) ----
  useEffect(() => {
    const target = getTargetForDate(new Date())
    if (viewRef.current) viewRef.current.scrollLeft = target
    initTimeline(target, dateDisplayed ?? new Date())
  }, [])
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setSource('user-scroll')

    const rawScroll = e.currentTarget.scrollLeft
    const x = clampOffset(rawScroll)
    if (rawScroll > x && viewRef.current) {
      viewRef.current.scrollLeft = x
      setOriginalOffset(x)
    }

    setOffsetFromUser(x)
  }

  useEffect(() => {
    if (source !== 'header') return
    if (!Array.isArray(days) || days.length === 0) return

    const key = dateDisplayed?.toISOString().slice(0, 10)
    if (!key || !days.includes(key)) return

    const target = getTargetForDate(dateDisplayed)
    if (viewRef.current) viewRef.current.scrollTo({ left: target })
    setOffsetProgrammatic(target)
    // setOriginalOffset(target)
  }, [dateDisplayed, days, source])

  const isCurrentTime = Math.round(originalOffset) === Math.round(offsetX)
  const atStart = offsetX <= 0
  const atEnd = offsetX > 120500

  const goPrev = () => {
    const el = viewRef.current
    if (!el || atStart) return
    const target = clampOffset(el.scrollLeft - step)
    el.scrollTo({ left: target, behavior: 'smooth' })
    setOffsetProgrammatic(target)
  }

  const goNext = () => {
    const el = viewRef.current
    if (!el || atEnd) return
    const target = clampOffset(el.scrollLeft + step)
    el.scrollTo({ left: target, behavior: 'smooth' })
    setOffsetProgrammatic(target)
  }

  return (
    <div
      className="view-controls"
      ref={(node) => {
        viewRef.current = node
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(inViewRef as any).current = node
      }}
      onScroll={handleScroll}
      onTouchEnd={() => setTouched(true)}
    >
      <div
        className="slide-control"
        style={{
          opacity: isInView ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <button
          onClick={goPrev}
          className={`icon-seta-left ${atStart ? 'disabled' : ''}`}
          type="button"
        >
          <span className="screen-readers-only">
            Deslizar janela para a esquerda
          </span>
        </button>

        <button
          onClick={() => {
            setTimeout(() => {
              const clamped = getTargetForDate(new Date())
              setTouched(false)
              if (viewRef.current) viewRef.current.scrollLeft = clamped
              setOriginalOffset(clamped)
              setOffsetProgrammatic(clamped)
            }, 0)
          }}
          className="time-indicator"
          type="button"
        >
          {isCurrentTime && !touched ? (
            <>
              <span>Agora</span>
              <span>
                {now.toLocaleTimeString('pt-PT', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </>
          ) : (
            <>
              <span>
                {formatDate(dateDisplayed ?? new Date(), new Date(), true)}
              </span>
              {originalOffset < offsetX ? (
                <span>
                  <img src={future} alt="future" />
                </span>
              ) : (
                <span>
                  <img src={past} alt="past" />
                </span>
              )}
            </>
          )}
        </button>

        <button
          onClick={goNext}
          className={`icon-seta-right ${atEnd ? 'disabled' : ''}`}
          type="button"
        >
          <span className="screen-readers-only">
            Deslizar janela para a direita
          </span>
        </button>
      </div>

      <div
        className="view-controls-inner"
        style={{ width: `${contentWidth}px` }}
      >
        {children}
      </div>
    </div>
  )
}
